import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import ForceGraph3D from 'react-force-graph-3d';
import SpriteText from 'three-spritetext';
import * as d3Force3D from 'd3-force-3d';
import { graphAPI } from '../services/api';
import { 
  Network, 
  Search, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut,
  Eye,
  EyeOff,
  Settings,
  Download,
  Move,
  Box,
  Boxes,
  List,
  X
} from 'lucide-react';

const Graph = () => {
  const navigate = useNavigate();
  const fgRef = useRef();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [filteredGraphData, setFilteredGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  // Always show hosts and relations (toggles removed)
  const [minConnections, setMinConnections] = useState(1);
  
  // Graph controls
  const [selectedNode, setSelectedNode] = useState(null);
  const [highlightedNodes, setHighlightedNodes] = useState(new Set());
  const [highlightedLinks, setHighlightedLinks] = useState(new Set());
  const [is3D, setIs3D] = useState(false); // 2D/3D mode toggle
  const [linkDistanceMultiplier, setLinkDistanceMultiplier] = useState(5.0); // Multiplier for link distance (higher default for better spacing)
  const [clusterDistanceMultiplier, setClusterDistanceMultiplier] = useState(8.0); // Multiplier for cluster spacing (charge strength) (higher default for better spacing)
  const [focusedNodeId, setFocusedNodeId] = useState(null); // Node to focus on with its connections
  const [showNodeSelector, setShowNodeSelector] = useState(false); // Show node selector menu
  const [showAllConnections, setShowAllConnections] = useState(false); // Show all connections between connected nodes
  const [nodeSearchQuery, setNodeSearchQuery] = useState(''); // Text search in node selector
  const [arrowSizeMultiplier, setArrowSizeMultiplier] = useState(1.0); // 0.5-2.5 — масштаб стрелок направления

  // Fetch graph data from API
  useEffect(() => {
    fetchGraphRelations();
  }, [minConnections]);

  // Apply client-side filters (focusedNode)
  useEffect(() => {
    applyFilters(graphData);
  }, [focusedNodeId, showAllConnections, graphData]);

  // Centralized function to update graph forces based on distance multipliers
  const updateGraphForces = useCallback(() => {
    // Skip manual force updates for 3D to prevent crashes in three-forcegraph
    // 3D mode relies on props (linkDistance, chargeStrength) which are handled by the library
    if (is3D) return;

    if (fgRef.current && filteredGraphData.nodes.length > 0) {
      const nodeCount = filteredGraphData.nodes.length;
      const baseDistance = nodeCount > 200 ? 200 : nodeCount > 100 ? 170 : 140;
      const actualLinkDistance = baseDistance * linkDistanceMultiplier;
      
      const baseCharge = nodeCount > 200 ? -500 : nodeCount > 100 ? -400 : -300;
      const actualCharge = baseCharge * clusterDistanceMultiplier;
      
      // Update forces safely to prevent crashes while maintaining interactivity
      try {
        const linkForce = fgRef.current.d3Force('link');
        if (linkForce) {
          linkForce.distance(actualLinkDistance);
          linkForce.iterations(1);
        }

        const chargeForce = fgRef.current.d3Force('charge');
        if (chargeForce) {
          chargeForce.strength(actualCharge);
          chargeForce.distanceMax(1000); // Добавлена дальность действия
        }

        // Collision force - предотвращение наложения узлов
        let collisionForce = fgRef.current.d3Force('collision');
        if (!collisionForce) {
          const d3 = require('d3-force');
          collisionForce = d3.forceCollide()
            .radius(node => (node.size || 5) * 3) // Радиус столкновения
            .strength(0.8);
          fgRef.current.d3Force('collision', collisionForce);
        }

        const centerForce = fgRef.current.d3Force('center');
        if (centerForce) {
          centerForce.strength(0.05); // Слабее притяжение к центру
        }
        
        // Reheat the simulation to apply changes
        if (fgRef.current.d3ReheatSimulation) {
          fgRef.current.d3ReheatSimulation();
        }
        
        console.log('Параметры сил графа обновлены:', {
          nodeCount,
          baseDistance,
          actualLinkDistance,
          actualCharge,
          linkDistanceMultiplier,
          clusterDistanceMultiplier
        });
      } catch (error) {
        console.warn('Ошибка обновления сил графа:', error);
      }
    }
  }, [filteredGraphData, linkDistanceMultiplier, clusterDistanceMultiplier, is3D]);

  // Function to update 3D graph forces using d3-force-3d
  const updateGraphForces3D = useCallback(() => {
    if (!is3D || !fgRef.current || filteredGraphData.nodes.length === 0) return;

    // Проверяем, что граф готов и имеет метод d3Force
    if (!fgRef.current.d3Force || typeof fgRef.current.d3Force !== 'function') {
      console.warn('Граф ещё не готов, метод d3Force недоступен');
      return;
    }

    // Проверяем, что d3-force-3d правильно импортирован
    if (!d3Force3D || typeof d3Force3D.forceLink !== 'function') {
      console.error('d3-force-3d импортирован некорректно. Доступные методы:', Object.keys(d3Force3D || {}));
      return;
    }

    try {
      const nodeCount = filteredGraphData.nodes.length;

      // Helper: визуальный радиус узла
      const nodeVisualRadius = (node) => {
        const base = node.size || 5;
        return Math.max(3, base * 1.2 * 2); // соответствует nodeVal
      };

      // --- Link force ---
      const avgLinkDistance = (() => {
        if (nodeCount > 1000) return 250 * linkDistanceMultiplier;
        if (nodeCount > 300) return 180 * linkDistanceMultiplier;
        if (nodeCount > 100) return 140 * linkDistanceMultiplier;
        return 100 * linkDistanceMultiplier;
      })();

      // Создаем link force с правильным API
      const linkForce = d3Force3D.forceLink(filteredGraphData.links)
        .id(d => d.id)
        .distance(() => avgLinkDistance)
        .strength(0.8);
      
      if (!linkForce) {
        console.error('Не удалось создать силу связей');
        return;
      }
      
      fgRef.current.d3Force('link', linkForce);

      // --- Charge (отталкивание) ---
      const baseCharge = (() => {
        if (nodeCount > 1000) return -1800;
        if (nodeCount > 300) return -1200;
        if (nodeCount > 100) return -900;
        return -600;
      })();
      
      const chargeForce = d3Force3D.forceManyBody()
        .strength(baseCharge * clusterDistanceMultiplier)
        .distanceMax(2000);
      
      if (!chargeForce) {
        console.error('Не удалось создать силу отталкивания');
        return;
      }
      
      fgRef.current.d3Force('charge', chargeForce);

      // --- Collision (важно для 3D: используем d3-force-3d.forceCollide) ---
      // forceCollide принимает функцию или число как радиус
      const collisionForce = d3Force3D.forceCollide()
        .radius(node => {
          // Используем сохраненный радиус или вычисляем
          const visualRadius = node._collisionRadius || nodeVisualRadius(node);
          return visualRadius + 6; // padding для предотвращения наложения
        })
        .strength(0.7)
        .iterations(nodeCount > 500 ? 1 : nodeCount > 300 ? 2 : 3);
      
      if (!collisionForce) {
        console.error('Не удалось создать силу коллизий');
        return;
      }
      
      fgRef.current.d3Force('collision', collisionForce);

      // --- Center force ---
      const centerForce = d3Force3D.forceCenter(0, 0, 0);
      
      if (!centerForce) {
        console.error('Не удалось создать центровочную силу');
        return;
      }
      
      fgRef.current.d3Force('center', centerForce);

      // Проверяем, что все силы установлены правильно
      const linkCheck = fgRef.current.d3Force('link');
      const chargeCheck = fgRef.current.d3Force('charge');
      const collisionCheck = fgRef.current.d3Force('collision');
      const centerCheck = fgRef.current.d3Force('center');

      if (!linkCheck || !chargeCheck || !collisionCheck || !centerCheck) {
        console.error('Некоторые силы не определены:', {
          link: !!linkCheck,
          charge: !!chargeCheck,
          collision: !!collisionCheck,
          center: !!centerCheck
        });
        return;
      }

      // Перезапускаем/разогреваем симуляцию
      if (fgRef.current.d3ReheatSimulation) {
        fgRef.current.d3ReheatSimulation();
      }

      console.log('Параметры 3D-сил графа обновлены:', {
        nodeCount,
        avgLinkDistance,
        baseCharge: baseCharge * clusterDistanceMultiplier,
        linkDistanceMultiplier,
        clusterDistanceMultiplier,
        forcesValid: {
          link: !!linkCheck,
          charge: !!chargeCheck,
          collision: !!collisionCheck,
          center: !!centerCheck
        }
      });
    } catch (error) {
      console.error('Ошибка обновления 3D-сил графа:', error);
      console.error('Детали ошибки:', error.stack);
    }
  }, [filteredGraphData, linkDistanceMultiplier, clusterDistanceMultiplier, is3D]);

  // Track previous node/link count to detect actual data changes (not just force updates)
  const prevDataRef = useRef({ nodes: 0, links: 0 });
  
  // Force graph reheat when filtered data changes (only on actual data change, not force updates)
  useEffect(() => {
    if (fgRef.current && filteredGraphData.nodes.length > 0) {
      const nodeCount = filteredGraphData.nodes.length;
      const linkCount = filteredGraphData.links.length;
      const prevCount = prevDataRef.current;
      
      // Only auto-fit if data actually changed (not just forces)
      const dataChanged = prevCount.nodes !== nodeCount || prevCount.links !== linkCount;
      
      if (dataChanged) {
        console.log('Перезапуск прогрева графа:', nodeCount, 'узлов и', linkCount, 'связей');
        prevDataRef.current = { nodes: nodeCount, links: linkCount };
        
        // Для 3D даем время на инициализацию графа
        if (is3D) {
          setTimeout(() => {
            if (fgRef.current && fgRef.current.d3Force) {
              updateGraphForces3D();
            }
          }, 100);
        } else {
          updateGraphForces();
        }
        
        // Auto-fit graph based on node count only when data changes
        setTimeout(() => {
          if (fgRef.current) {
            if (is3D) {
              // For 3D, adjust camera distance based on node count
              const baseDistance = nodeCount > 500 ? 800 : nodeCount > 200 ? 600 : nodeCount > 100 ? 400 : 200;
              fgRef.current.cameraPosition({ x: 0, y: 0, z: baseDistance });
            } else {
              // For 2D, adjust zoom based on node count
              const padding = nodeCount > 500 ? 100 : nodeCount > 200 ? 200 : nodeCount > 100 ? 300 : 400;
              fgRef.current.zoomToFit(padding, 1000);
            }
          }
        }, 500);
      } else {
        // Just update forces without changing camera/zoom
        if (is3D) {
          // Для 3D даем время на инициализацию
          setTimeout(() => {
            if (fgRef.current && fgRef.current.d3Force) {
              updateGraphForces3D();
            }
          }, 50);
        } else {
          updateGraphForces();
        }
      }
    }
  }, [filteredGraphData, updateGraphForces, updateGraphForces3D, is3D]);

  // Apply distance changes when multipliers change
  useEffect(() => {
    if (is3D) {
      // Для 3D даем больше времени на инициализацию и проверяем готовность графа
      const checkAndUpdate = () => {
        if (fgRef.current && 
            fgRef.current.d3Force && 
            typeof fgRef.current.d3Force === 'function' &&
            filteredGraphData.nodes.length > 0) {
          // Дополнительная проверка - убеждаемся что силы уже инициализированы
          const existingLink = fgRef.current.d3Force('link');
          if (existingLink) {
            updateGraphForces3D();
          } else {
            // Если силы еще не инициализированы, ждем еще
            setTimeout(checkAndUpdate, 100);
          }
        } else {
          // Если граф еще не готов, ждем еще
          setTimeout(checkAndUpdate, 100);
        }
      };
      setTimeout(checkAndUpdate, 150);
    } else {
      updateGraphForces();
    }
  }, [linkDistanceMultiplier, clusterDistanceMultiplier, updateGraphForces, updateGraphForces3D, is3D, filteredGraphData]);

  // Update forces when switching between 2D/3D modes
  useEffect(() => {
    if (is3D && fgRef.current && filteredGraphData.nodes.length > 0) {
      // Delay to ensure the graph is fully mounted and initialized
      setTimeout(() => {
        if (fgRef.current && fgRef.current.d3Force) {
          updateGraphForces3D();
        }
      }, 200);
    }
  }, [is3D, updateGraphForces3D, filteredGraphData]);

  // Настройка controls для увеличенного диапазона zoom в 3D
  useEffect(() => {
    if (is3D && fgRef.current) {
      const setupControls = () => {
        if (fgRef.current && fgRef.current.controls) {
          const controls = fgRef.current.controls;
          // Увеличиваем минимальное и максимальное расстояние для zoom
          if (controls.minDistance !== undefined) {
            controls.minDistance = 50;   // Минимальное расстояние (максимальное приближение)
          }
          if (controls.maxDistance !== undefined) {
            controls.maxDistance = 5000;  // Максимальное расстояние (максимальное отдаление)
          }
        } else {
          // Если controls еще не готовы, пробуем еще раз
          setTimeout(setupControls, 100);
        }
      };
      setTimeout(setupControls, 300);
    }
  }, [is3D, filteredGraphData]);

  const fetchGraphRelations = async () => {
    try {
      setLoading(true);
      const response = await graphAPI.getRelations('', minConnections);
      const data = {
        nodes: response.data.nodes.map(node => ({
          ...node,
          color: '#3b82f6' // Set color for visualization
        })),
        links: response.data.links.map(link => ({
          ...link,
          color: '#ef4444' // Set color for visualization
        }))
      };
      setGraphData(data);
      applyFilters(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить данные графа');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data) => {
    let filteredNodes = [...data.nodes];
    let filteredLinks = [...data.links];

    // Filter by focused node (if set) - client-side filtering for interactivity
    if (focusedNodeId) {
      // Find all nodes directly connected to the focused node
      const connectedNodeIds = new Set([focusedNodeId]);
      
      // First, find all directly connected nodes
      data.links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        
        if (sourceId === focusedNodeId) {
          connectedNodeIds.add(targetId);
        }
        if (targetId === focusedNodeId) {
          connectedNodeIds.add(sourceId);
        }
      });
      
      // Filter nodes
      filteredNodes = filteredNodes.filter(node => connectedNodeIds.has(node.id));
      
      // Filter links based on showAllConnections flag
      if (showAllConnections) {
        // Show all links between connected nodes
        filteredLinks = filteredLinks.filter(link => {
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
          const targetId = typeof link.target === 'object' ? link.target.id : link.target;
          return connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId);
        });
      } else {
        // Show only direct links from/to focused node
        filteredLinks = filteredLinks.filter(link => {
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
          const targetId = typeof link.target === 'object' ? link.target.id : link.target;
          return sourceId === focusedNodeId || targetId === focusedNodeId;
        });
      }
    }
    // Note: domainFilter and minConnections are now handled server-side
    // No need to filter here as data already comes filtered from API

    // Create new objects to ensure React detects the change
    const newFilteredData = {
      nodes: filteredNodes.map(node => ({ ...node })),
      links: filteredLinks.map(link => ({ ...link }))
    };

    setFilteredGraphData(newFilteredData);
  };

  const handleNodeClick = useCallback((node) => {
    // Левый клик открывает side-panel с соседями. Навигация в /hosts
    // выполняется по отдельной кнопке внутри панели (см. ниже).
    setSelectedNode(node);
  }, []);

  const openHostInHostsPage = useCallback((nodeName) => {
    if (!nodeName) return;
    const regexFilter = `^${nodeName.replace(/\./g, '\\.')}$`;
    navigate(`/hosts?filter=${encodeURIComponent(regexFilter)}`);
  }, [navigate]);

  const handleNodeRightClick = useCallback((node) => {
    // Toggle focus on this node and its connections
    if (focusedNodeId === node.id) {
      // If right-clicking the same node, unfocus
      setFocusedNodeId(null);
    } else {
      // Focus on right-clicked node
      setFocusedNodeId(node.id);
    }
  }, [focusedNodeId]);

  const handleNodeHover = useCallback((node, prevNode) => {
    if (node) {
      // Highlight connected nodes
      const connectedNodes = new Set();
      const connectedLinks = new Set();
      
      filteredGraphData.links.forEach(link => {
        if (link.source === node.id || link.target === node.id) {
          connectedNodes.add(link.source);
          connectedNodes.add(link.target);
          connectedLinks.add(link);
        }
      });
      
      setHighlightedNodes(connectedNodes);
      setHighlightedLinks(connectedLinks);
    } else {
      setHighlightedNodes(new Set());
      setHighlightedLinks(new Set());
    }
  }, [filteredGraphData.links]);

  const focusOnNode = useCallback((nodeId) => {
    setFocusedNodeId(nodeId);
    
    // After filtering, center on the node
    setTimeout(() => {
      if (fgRef.current && filteredGraphData.nodes.length > 0) {
        const node = filteredGraphData.nodes.find(n => n.id === nodeId);
        if (node) {
          if (is3D) {
            // For 3D, position camera to look at the node
            const distance = 500;
            fgRef.current.cameraPosition(
              { x: (node.x || 0) + distance, y: (node.y || 0) + distance, z: (node.z || 0) + distance },
              { x: node.x || 0, y: node.y || 0, z: node.z || 0 },
              2000
            );
          } else {
            // For 2D, center on the node
            fgRef.current.centerAt(node.x || 0, node.y || 0, 1000);
            fgRef.current.zoom(2, 1000);
          }
        }
      }
    }, 100);
  }, [filteredGraphData, is3D]);

  const resetGraph = () => {
    if (fgRef.current) {
      if (is3D) {
        // For 3D mode, reset camera position
        fgRef.current.cameraPosition({ x: 0, y: 0, z: 1000 });
      } else {
        // For 2D mode, use zoomToFit
        fgRef.current.zoomToFit(400);
      }
      updateGraphForces();
    }
    setSelectedNode(null);
    setHighlightedNodes(new Set());
    setHighlightedLinks(new Set());
    setFocusedNodeId(null);
  };

  const zoomIn = () => {
    if (fgRef.current) {
      fgRef.current.zoom(1.2, 400);
    }
  };

  const zoomOut = () => {
    if (fgRef.current) {
      fgRef.current.zoom(0.8, 400);
    }
  };

  const normalizedNodeSearch = nodeSearchQuery.trim().toLowerCase();
  const selectorNodes = [...graphData.nodes]
    .filter((node) => {
      if (!normalizedNodeSearch) return true;
      const name = String(node.name || '').toLowerCase();
      const id = String(node.id || '').toLowerCase();
      const type = String(node.type || '').toLowerCase();
      return (
        name.includes(normalizedNodeSearch) ||
        id.includes(normalizedNodeSearch) ||
        type.includes(normalizedNodeSearch)
      );
    })
    .sort((a, b) => (b.connections || 0) - (a.connections || 0));

  if (loading) {
    return (
      <div className="loading">
        <div>Загрузка данных графа...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: 'var(--text-primary)',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <Network size={32} color="#3b82f6" />
          Сетевой граф
        </h1>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '16px',
        }}>
          Интерактивная визуализация хостов и их связей ({filteredGraphData.nodes.length} узлов, {filteredGraphData.links.length} связей)
          <br />
          <span style={{ fontSize: '14px', fontStyle: 'italic' }}>
            ЛКМ: открыть в Хостах | ПКМ: изолировать узел и его связи
          </span>
        </p>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Legend */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <h3 style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Eye size={16} />
          Легенда
        </h3>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '16px', 
              height: '16px', 
              borderRadius: '50%', 
              backgroundColor: '#3b82f6',
              border: '2px solid #ffffff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}></div>
            <span style={{ color: 'var(--text-secondary)' }}>Хосты</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '32px', 
              height: '4px', 
              backgroundColor: '#ef4444',
              opacity: 0.8,
              borderRadius: '2px'
            }}></div>
            <span style={{ color: 'var(--text-secondary)' }}>Связи</span>
          </div>
        </div>
      </div>

      {/* Focused Node Info */}
      {focusedNodeId && (
        <div className="card" style={{ 
          marginBottom: '16px', 
          padding: '12px 16px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid #3b82f6'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: 'var(--text-primary)'
            }}>
              <Eye size={16} color="#3b82f6" />
              <span>
                Показан только <strong>{selectedNode?.name || 'выбранный узел'}</strong> и его связи
              </span>
            </div>
            <button
              onClick={() => setFocusedNodeId(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: '#3b82f6',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
              title="Показать все узлы"
            >
              Показать все
            </button>
          </div>
        </div>
      )}

      {/* Node Selector Menu */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <button
            onClick={() => {
              setShowNodeSelector((prev) => {
                const next = !prev;
                if (!next) {
                  setNodeSearchQuery('');
                }
                return next;
              });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: showNodeSelector ? '#3b82f6' : 'var(--bg-secondary)',
              color: showNodeSelector ? '#ffffff' : 'var(--text-primary)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
            title="Выберите узел для фокуса и отображения его связей"
          >
            <List size={16} />
            {showNodeSelector ? 'Скрыть выбор узла' : 'Выбрать узел'}
          </button>
          
          {showNodeSelector && (
            <div style={{
              flex: 1,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
              }}>
                <div style={{
                  padding: '8px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    Выберите узел (показано: {selectorNodes.length} из {graphData.nodes.length})
                  </span>
                  <button
                    onClick={() => {
                      setShowNodeSelector(false);
                      setNodeSearchQuery('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      padding: '4px',
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div style={{
                  padding: '8px',
                  borderBottom: '1px solid var(--border-color)',
                  position: 'relative',
                }}>
                  <Search
                    size={14}
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-secondary)',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Поиск по имени узла..."
                    value={nodeSearchQuery}
                    onChange={(e) => setNodeSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 8px 8px 32px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {selectorNodes.length === 0 && (
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '13px',
                    }}>
                      Ничего не найдено
                    </div>
                  )}
                  {selectorNodes.map(node => (
                      <div
                        key={node.id}
                        onClick={() => {
                          focusOnNode(node.id);
                          setShowNodeSelector(false);
                          setNodeSearchQuery('');
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border-color)',
                          backgroundColor: focusedNodeId === node.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = focusedNodeId === node.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: 'var(--text-primary)',
                            }}>
                              {node.name}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: 'var(--text-secondary)',
                              marginTop: '2px',
                            }}>
                              {node.type} • связей: {node.connections || 0}
                            </div>
                          </div>
                          {focusedNodeId === node.id && (
                            <Eye size={16} color="#3b82f6" />
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Focused Node Controls */}
      {focusedNodeId && (
        <div className="card" style={{ marginBottom: '16px', padding: '12px 16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={showAllConnections}
                onChange={(e) => setShowAllConnections(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                }}
              />
              <span>Показывать все связи между связанными узлами</span>
            </label>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center',
        }}>
          {/* Node type toggles removed: always showing hosts and relations */}

          {/* Min Connections Filter */}
          <div style={{ minWidth: '150px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Мин. связей
            </label>
            <input
              type="number"
              min="0"
              value={minConnections}
              onChange={(e) => setMinConnections(parseInt(e.target.value) || 0)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Arrow Size Control */}
          <div style={{ minWidth: '180px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Размер стрелок: {arrowSizeMultiplier.toFixed(1)}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2.5"
              step="0.1"
              value={arrowSizeMultiplier}
              onChange={(e) => setArrowSizeMultiplier(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Размер стрелок направления (0.5x — 2.5x)"
            />
          </div>

          {/* Link Distance Control */}
          <div style={{ minWidth: '200px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              <Move size={14} />
              Дистанция связей: {linkDistanceMultiplier.toFixed(1)}x
            </label>
            <input
              type="range"
              min="0.5"
              max="30.0"
              step="0.1"
              value={linkDistanceMultiplier}
              onChange={(e) => setLinkDistanceMultiplier(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Настроить расстояние между связанными узлами (0.5x - 30.0x)"
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              marginTop: '2px',
            }}>
              <span>Коротко</span>
              <span>Далеко</span>
            </div>
          </div>

          {/* Cluster Distance Control */}
          <div style={{ minWidth: '200px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              <Boxes size={14} />
              Дистанция кластеров: {clusterDistanceMultiplier.toFixed(1)}x
            </label>
            <input
              type="range"
              min="0.5"
              max="30.0"
              step="0.1"
              value={clusterDistanceMultiplier}
              onChange={(e) => setClusterDistanceMultiplier(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Настроить расстояние между кластерами узлов (0.5x - 30.0x)"
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              marginTop: '2px',
            }}>
              <span>Плотно</span>
              <span>Свободно</span>
            </div>
          </div>


          {/* Graph Controls */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIs3D(!is3D)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: is3D ? '#3b82f6' : 'var(--bg-secondary)',
                color: is3D ? '#ffffff' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: is3D ? '600' : '400',
              }}
              title={is3D ? "Переключить на 2D" : "Переключить на 3D"}
            >
              {is3D ? <Box size={16} /> : <Boxes size={16} />}
              {is3D ? '3D' : '2D'}
            </button>
            
            <button
              onClick={resetGraph}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
              title="Сбросить вид"
            >
              <RotateCcw size={16} />
            </button>
            
            {!is3D && (
              <>
                <button
                  onClick={zoomIn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                  title="Увеличить"
                >
                  <ZoomIn size={16} />
                </button>
                
                <button
                  onClick={zoomOut}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                  title="Уменьшить"
                >
                  <ZoomOut size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Graph Container */}
      <div style={{ 
        flex: 1,
        width: '100%',
        height: '700px',
        border: '1px solid var(--border-color)', 
        borderRadius: '8px',
        backgroundColor: 'var(--bg-secondary)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {is3D ? (
          <ForceGraph3D
            key={`graph-3d-${filteredGraphData.nodes.length}-${filteredGraphData.links.length}-${linkDistanceMultiplier.toFixed(1)}-${clusterDistanceMultiplier.toFixed(1)}`}
            ref={fgRef}
            graphData={filteredGraphData}
            nodeLabel={node => `${node.name}\nТип: ${node.type}\nСвязей: ${node.connections}`}
            linkLabel={link => `Связь: ${link.type}`}
            nodeColor={node => node.color || '#3b82f6'}
            linkColor={link => {
              // Менее прозрачные цвета для стрелок
              if (link.type === 'host-domain') {
                return '#10b981';
              } else if (link.type === 'host-relation') {
                return '#ef4444';
              }
              return link.color || '#ef4444';
            }}
            linkWidth={link => 3}
            linkDirectionalArrowLength={72 * arrowSizeMultiplier}
            linkDirectionalArrowRelPos={0.95}
            linkDirectionalArrowResolution={16}
            linkOpacity={0.9}
            nodeVal={node => (node.size || 5) * 1.2}
            nodeThreeObject={node => {
              // Вычисляем визуальный радиус и сохраняем для collision
              const base = node.size || 5;
              const visualRadius = Math.max(3, base * 1.2 * 2);
              node._collisionRadius = visualRadius;
              
              const sprite = new SpriteText(node.name);
              sprite.color = node.color || '#3b82f6';
              sprite.textHeight = ((node.size || 5) * 0.5 + 4) * 2; // Увеличено в 2 раза
              sprite.padding = 4;
              sprite.backgroundColor = 'rgba(0, 0, 0, 0.5)';
              sprite.borderRadius = 4;
              return sprite;
            }}
            nodeThreeObjectExtend={true} // Extend the node object (sphere) with the sprite
            onNodeClick={handleNodeClick}
            onNodeRightClick={handleNodeRightClick}
            onNodeHover={handleNodeHover}
            width={typeof window !== 'undefined' ? window.innerWidth - 300 : 1200}
            height={700}
            warmupTicks={100}
            cooldownTicks={1000}
            d3AlphaDecay={0.01}
            d3VelocityDecay={0.3}
            enableNodeDrag={true}
            controlType="trackball"
            enableNavigationControls={true}
          />
        ) : (
          <ForceGraph2D
            key={`graph-2d-${filteredGraphData.nodes.length}-${filteredGraphData.links.length}`}
            ref={fgRef}
            graphData={filteredGraphData}
            nodeLabel={node => `${node.name}\nТип: ${node.type}\nСвязей: ${node.connections}`}
            linkLabel={link => `Связь: ${link.type}`}
            linkCanvasObjectMode={() => 'replace'}
            linkDirectionalArrowLength={0}
            nodeCanvasObject={(node, ctx, globalScale) => {
              const label = node.name;
              const fontSize = Math.max(11, 15/globalScale);
              ctx.font = `bold ${fontSize}px Sans-Serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              // Увеличенный размер узла для лучшей видимости
              const nodeSize = node.size * 1.2;
              
              // Draw shadow for depth
              ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
              ctx.shadowBlur = 10;
              ctx.shadowOffsetX = 2;
              ctx.shadowOffsetY = 2;
              
              // Draw node
              ctx.fillStyle = node.color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI, false);
              ctx.fill();
              
              // Reset shadow
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              
              // Add white border
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 3;
              ctx.stroke();
              
              // Add inner border for more contrast
              ctx.strokeStyle = node.color;
              ctx.lineWidth = 1;
              ctx.stroke();
              
              // Add label with background for better visibility
              // На далеком масштабе показываем более крупные метки для группировки
              const showLabel = globalScale > 0.15;
              
              if (showLabel) {
                const textY = node.y + nodeSize + fontSize + 12;
                const textWidth = ctx.measureText(label).width;
                const padding = 8;
                
                // На далеком масштабе делаем более яркие и заметные метки
                const labelOpacity = globalScale < 0.4 ? 1.0 : 0.95;
                const bgColor = globalScale < 0.4 
                  ? (node.type === 'host' ? 'rgba(59, 130, 246, 0.95)' : 'rgba(16, 185, 129, 0.95)')
                  : 'rgba(255, 255, 255, 0.95)';
                const textColor = globalScale < 0.4 ? '#ffffff' : '#000000';
                
                // Draw shadow for label background
                ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
                ctx.shadowBlur = globalScale < 0.4 ? 8 : 4;
                
                // Draw background rectangle with rounded corners
                const rectX = node.x - textWidth/2 - padding;
                const rectY = textY - fontSize/2 - padding;
                const rectWidth = textWidth + padding*2;
                const rectHeight = fontSize + padding*2;
                const radius = 4;
                
                ctx.fillStyle = bgColor;
                ctx.globalAlpha = labelOpacity;
                ctx.beginPath();
                ctx.moveTo(rectX + radius, rectY);
                ctx.lineTo(rectX + rectWidth - radius, rectY);
                ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + radius);
                ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius);
                ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - radius, rectY + rectHeight);
                ctx.lineTo(rectX + radius, rectY + rectHeight);
                ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - radius);
                ctx.lineTo(rectX, rectY + radius);
                ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
                ctx.closePath();
                ctx.fill();
                
                // Reset shadow and alpha
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1.0;
                
                // Draw text
                ctx.fillStyle = textColor;
                ctx.fillText(label, node.x, textY);
                
                // Add small type indicator
                if (globalScale > 0.5) {
                  const typeLabel = node.type === 'host' ? 'H' : 'D';
                  const typeFontSize = fontSize * 0.7;
                  ctx.font = `bold ${typeFontSize}px Sans-Serif`;
                  ctx.fillStyle = '#ffffff';
                  ctx.fillText(typeLabel, node.x, node.y);
                }
              }
            }}
            linkCanvasObject={(link, ctx, globalScale) => {
              const { source, target } = link;
              if (!source || !target || typeof source.x !== 'number' || typeof target.x !== 'number') return;

              // Цвет/прозрачность по типу связи
              let strokeColor = link.color || '#ef4444';
              let strokeOpacity = 0.7;
              if (link.type === 'host-domain') {
                strokeColor = '#10b981';
                strokeOpacity = 0.7;
              } else if (link.type === 'host-relation') {
                strokeColor = '#ef4444';
                strokeOpacity = 0.85;
              }

              const dx = target.x - source.x;
              const dy = target.y - source.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < 0.001) return;
              const ux = dx / dist;
              const uy = dy / dist;

              // Радиус target-узла + небольшой отступ — стрелка торчит ЗА его границу
              const targetR = (target.size || 5) * 1.2 + 3;
              const tipX = target.x - ux * targetR;
              const tipY = target.y - uy * targetR;

              // Размеры стрелки (выраженные в графовых координатах, не зависят от zoom —
              // визуально остаются крупными даже при отзуме)
              const baseArrowLen = 24 * arrowSizeMultiplier;
              const baseArrowWidth = baseArrowLen * 0.55;
              // На сильном отзуме (globalScale<1) увеличиваем чтобы оставались видимыми
              const zoomCompensation = globalScale < 1 ? 1 / Math.max(globalScale, 0.2) : 1;
              const arrowLen = baseArrowLen * zoomCompensation;
              const arrowWidth = baseArrowWidth * zoomCompensation;

              // Точка основания стрелки (где заканчивается линия и начинается треугольник)
              const baseX = tipX - ux * arrowLen;
              const baseY = tipY - uy * arrowLen;
              // Перпендикуляр для крыльев стрелки
              const px = -uy;
              const py = ux;

              // 1. Линия от source до основания стрелки (НЕ до target — оставляем место для треугольника)
              const lineWidth = Math.max(2, 3 / globalScale);
              ctx.strokeStyle = strokeColor;
              ctx.globalAlpha = strokeOpacity;
              ctx.lineWidth = lineWidth;
              ctx.beginPath();
              ctx.moveTo(source.x, source.y);
              ctx.lineTo(baseX, baseY);
              ctx.stroke();

              // 2. Стрелка-треугольник: tip в tipX/tipY, крылья отгибаются перпендикулярно
              ctx.globalAlpha = Math.min(1.0, strokeOpacity + 0.15);
              ctx.fillStyle = strokeColor;
              ctx.beginPath();
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(baseX + px * arrowWidth / 2, baseY + py * arrowWidth / 2);
              ctx.lineTo(baseX - px * arrowWidth / 2, baseY - py * arrowWidth / 2);
              ctx.closePath();
              ctx.fill();
              // Лёгкая обводка чтобы на цветных нодах не сливалась
              ctx.lineWidth = Math.max(0.5, 1 / globalScale);
              ctx.strokeStyle = strokeColor;
              ctx.stroke();

              ctx.globalAlpha = 1.0;
            }}
            onNodeClick={handleNodeClick}
            onNodeRightClick={handleNodeRightClick}
            onNodeHover={handleNodeHover}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, node.size + 5, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
            width={typeof window !== 'undefined' ? window.innerWidth - 300 : 1200}
            height={700}
            cooldownTicks={300}
            d3AlphaDecay={0.01}
            d3VelocityDecay={0.3}
            enableZoomPanInteraction={true}
            enableNodeDrag={true}
            linkDistance={link => {
              if (!link) return 50;
              const nodeCount = filteredGraphData.nodes.length;
              const baseDistance = nodeCount > 200 ? 100 : nodeCount > 100 ? 70 : 40;
              const distance = baseDistance * linkDistanceMultiplier;
              return isNaN(distance) || distance <= 0 ? 50 : distance;
            }}
          />
        )}
      </div>

      {/* Selected Node — детали с соседями и URL */}
      {selectedNode && (
        <NodeDetailPanel
          node={selectedNode}
          links={graphData.links}
          onClose={() => setSelectedNode(null)}
          onFocus={(id) => { setFocusedNodeId(id); }}
          onOpenInHosts={openHostInHostsPage}
        />
      )}
    </div>
  );
};

function NodeDetailPanel({ node, links, onClose, onFocus, onOpenInHosts }) {
  const [expanded, setExpanded] = useState({}); // key=neighborId, value=bool

  const outgoing = [];
  const incoming = [];
  for (const link of links || []) {
    const srcId = typeof link.source === 'object' ? link.source.id : link.source;
    const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
    if (srcId === node.id) {
      outgoing.push({ id: tgtId, name: (typeof link.target === 'object' ? link.target.name : tgtId.replace(/^host:/, '')), urls: link.urls || [] });
    } else if (tgtId === node.id) {
      incoming.push({ id: srcId, name: (typeof link.source === 'object' ? link.source.name : srcId.replace(/^host:/, '')), urls: link.urls || [] });
    }
  }

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const copyUrls = (urls) => {
    try {
      navigator.clipboard.writeText(urls.join('\n'));
    } catch (_) { /* no-op */ }
  };

  const renderNeighborGroup = (label, neighbors, accent) => (
    <div style={{ marginTop: '12px' }}>
      <div style={{
        fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
      }}>
        {label} ({neighbors.length})
      </div>
      {neighbors.length === 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', padding: '4px 0' }}>
          Нет связей
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto' }}>
          {neighbors.map((nb) => (
            <div key={nb.id} style={{
              border: '1px solid var(--border-color)', borderRadius: '6px',
              padding: '6px 10px', backgroundColor: 'var(--bg-secondary)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: accent, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {nb.name}
                  </span>
                  {nb.urls.length > 0 && (
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      ({nb.urls.length} URL)
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  {nb.urls.length > 0 && (
                    <button
                      onClick={() => toggle(nb.id)}
                      title={expanded[nb.id] ? 'Свернуть URL' : 'Показать URL'}
                      style={{ padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
                        border: '1px solid var(--border-color)', borderRadius: '4px',
                        background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                    >
                      {expanded[nb.id] ? '▾' : '▸'}
                    </button>
                  )}
                  <button
                    onClick={() => onFocus(nb.id)}
                    title="Сфокусировать на узле"
                    style={{ padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
                      border: '1px solid var(--border-color)', borderRadius: '4px',
                      background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  >
                    Фокус
                  </button>
                </div>
              </div>
              {expanded[nb.id] && nb.urls.length > 0 && (
                <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed var(--border-color)' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                    <button
                      onClick={() => copyUrls(nb.urls)}
                      style={{ padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
                        border: '1px solid var(--border-color)', borderRadius: '4px',
                        background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                    >
                      Копировать все
                    </button>
                  </div>
                  <ul style={{
                    margin: 0, padding: '0 0 0 16px', fontSize: '11px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: 'var(--text-primary)', wordBreak: 'break-all',
                  }}>
                    {nb.urls.map((u, i) => (
                      <li key={i} style={{ marginBottom: '2px' }}>
                        <a href={u} target="_blank" rel="noopener noreferrer"
                           style={{ color: 'var(--text-primary)' }}>
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="card" style={{ marginTop: '16px', position: 'relative' }}>
      <button
        onClick={onClose}
        title="Закрыть"
        style={{
          position: 'absolute', top: '10px', right: '10px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', padding: '4px',
        }}
      >
        <X size={16} />
      </button>
      <h3 style={{
        fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)',
        marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px',
        paddingRight: '24px',
      }}>
        <Settings size={16} />
        Узел: {node.name}
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '8px', fontSize: '13px',
      }}>
        <div><strong>Тип:</strong> {node.type}</div>
        <div><strong>Связей:</strong> {node.connections}</div>
        {typeof node.out_degree === 'number' && (
          <div><strong>Исходящих:</strong> {node.out_degree}</div>
        )}
        {typeof node.in_degree === 'number' && (
          <div><strong>Входящих:</strong> {node.in_degree}</div>
        )}
      </div>
      <div style={{ marginTop: '10px' }}>
        <button
          onClick={() => onOpenInHosts(node.name)}
          style={{ padding: '4px 12px', fontSize: '12px', cursor: 'pointer',
            border: '1px solid var(--border-color)', borderRadius: '4px',
            background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
        >
          Открыть в Hosts
        </button>
      </div>
      {renderNeighborGroup('Исходящие (этот → другие)', outgoing, '#ef4444')}
      {renderNeighborGroup('Входящие (другие → этот)', incoming, '#3b82f6')}
    </div>
  );
}

export default Graph;
